import { useCallback, useRef, useState } from 'react'
import * as Sentry from '@sentry/gatsby'
import type { Review } from 'src/components/restructure/product/types/review'
import { normalizeReviewResponse } from 'src/utils/reviews/normalizeReviewResponse'

type ReviewSummaryState = {
  review: Review | null
  isLoading: boolean
  error: Error | null
}

const initialState: ReviewSummaryState = {
  review: null,
  isLoading: false,
  error: null,
}

// Cache module-level por productId, compartilhado entre o card do carrinho
// (CartProductDetailsTrigger) e o miniPDP (MiniPdpInfoAccordion) para evitar
// buscar a mesma avaliação duas vezes.
const reviewSummaryCache = new Map<string, Review | null>()

export const clearProductReviewSummaryCache = () => {
  reviewSummaryCache.clear()
}

export function useProductReviewSummary(productId?: string | null) {
  const [state, setState] = useState<ReviewSummaryState>(initialState)
  const requestedProductIdRef = useRef<string | null>(null)

  const load = useCallback(async () => {
    if (!productId || requestedProductIdRef.current === productId) {
      return
    }

    requestedProductIdRef.current = productId

    const cachedReview = reviewSummaryCache.get(productId)

    if (cachedReview !== undefined) {
      setState({ review: cachedReview, isLoading: false, error: null })

      return
    }

    setState({ review: null, isLoading: true, error: null })

    try {
      const response = await fetch(
        `/api/getProductReview?productId=${encodeURIComponent(productId)}`
      )

      if (!response.ok) {
        throw new Error('Erro ao buscar avaliação do produto')
      }

      const data = await response.json()
      const normalizedReview = normalizeReviewResponse(data)

      reviewSummaryCache.set(productId, normalizedReview)
      setState({ review: normalizedReview, isLoading: false, error: null })
    } catch (error) {
      const normalizedError =
        error instanceof Error
          ? error
          : new Error('Erro ao buscar avaliação do produto')

      Sentry.captureException(normalizedError, {
        level: 'warning',
        tags: {
          component: 'useProductReviewSummary',
          source: 'product-reviews',
        },
        extra: { productId, endpoint: '/api/getProductReview' },
      })

      // Permite tentar de novo numa próxima chamada de load() (ex: reabrir o modal).
      requestedProductIdRef.current = null
      setState({ review: null, isLoading: false, error: normalizedError })
    }
  }, [productId])

  return { ...state, load }
}
